/**
 * DV-INF-001: Shared Demo Viewer Logging Utility
 * Standardized logging with correlation IDs and structured events
 */

import type { LogEntry } from '@/components/demo-viewer/LogsPanel';

/**
 * Demo Viewer log levels with consistent semantics
 */
export type DemoLogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Demo Viewer log sources for categorization
 */
export type DemoLogSource =
  | 'auth'
  | 'websocket'
  | 'temporal'
  | 'heartbeats'
  | 'feature-flags'
  | 'stage-outputs'
  | 'billing'
  | 'api'
  | 'ui'
  | 'system'
  | 'demo'
  | 'user'
  | 'stage_tester'; // ADDENDUM: Added for prompt preview logging

/**
 * Demo Viewer log actions for event tracking
 */
export type DemoLogAction =
  | 'init'
  | 'fetch_start'
  | 'fetch_success'
  | 'fetch_error'
  | 'fetch_cancelled'
  | 'user_action'
  | 'ui_error'
  | 'connection_open'
  | 'connection_close'
  | 'connection_error'
  | 'auth_check'
  | 'permission_denied'
  | 'page_change'
  | 'expand'
  | 'collapse'
  | 'rendered'
  | 'validated'
  | 'refresh'
  | 'clear'
  | 'test'
  | 'validation_start'
  | 'validation_success'
  | 'validation_error';

/**
 * Structured metadata for demo logs
 */
export interface DemoLogMeta {
  source: DemoLogSource;
  action?: DemoLogAction;
  correlationId: string;
  requestId: string;
  timestamp: number;
  component?: string;
  executionId?: string;
  duration?: number;
  error?: string;
  count?: number;
  status?: number;
  url?: string;
  [key: string]: any; // Allow additional metadata
}

/**
 * Demo Viewer log entry with standardized structure
 */
export interface DemoLogEntry extends LogEntry {
  meta: DemoLogMeta;
}

/**
 * Generate consistent correlation ID for request tracking
 */
export function generateCorrelationId(source: DemoLogSource, suffix?: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 5);
  const baseSuffix = suffix ? `-${suffix}` : '';
  return `${source}-${timestamp}-${random}${baseSuffix}`;
}

/**
 * Create standardized demo log entry
 */
export function createDemoLogEntry(
  source: DemoLogSource,
  message: string,
  level: DemoLogLevel = 'info',
  meta: Partial<DemoLogMeta> = {}
): DemoLogEntry {
  const timestamp = Date.now();
  const correlationId = meta.correlationId || generateCorrelationId(source);
  const requestId = meta.requestId || generateCorrelationId(source, 'req');

  return {
    id: `${timestamp}-${Math.random().toString(36).substr(2, 5)}`,
    ts: new Date().toISOString(),
    level,
    text: `[${source.toUpperCase()}] ${message}`,
    meta: {
      source,
      correlationId,
      requestId,
      timestamp,
      ...meta,
    } as DemoLogMeta,
  };
}

/**
 * Demo Viewer Logger Class
 * Provides consistent logging interface for demo components
 */
export class DemoLogger {
  private source: DemoLogSource;
  private component?: string;
  private onLog?: (entry: DemoLogEntry) => void;
  private correlationPrefix: string;
  private seenEntries: Map<string, number> = new Map(); // Deduplication cache

  constructor(
    source: DemoLogSource,
    options: {
      component?: string;
      onLog?: (entry: DemoLogEntry) => void;
    } = {}
  ) {
    this.source = source;
    this.component = options.component;
    this.onLog = options.onLog;
    this.correlationPrefix = `${source}-${Date.now()}`;
  }

  /**
   * Create correlation ID for this logger instance
   */
  private createCorrelationId(suffix?: string): string {
    const random = Math.random().toString(36).substr(2, 5);
    const suffixPart = suffix ? `-${suffix}` : '';
    return `${this.correlationPrefix}-${random}${suffixPart}`;
  }

  /**
   * Create deduplication key for burst suppression
   */
  private createDeduplicationKey(level: DemoLogLevel, message: string, meta?: Partial<DemoLogMeta>): string {
    return `${level}:${message}:${this.source}:${this.component || ''}:${meta?.stage || ''}`;
  }

  /**
   * Check if entry should be suppressed due to recent duplicate
   */
  private shouldSuppressDuplicate(level: DemoLogLevel, message: string, meta?: Partial<DemoLogMeta>): boolean {
    const key = this.createDeduplicationKey(level, message, meta);
    const now = Date.now();
    const lastSeen = this.seenEntries.get(key);

    // Clean up old entries periodically (older than 1 minute)
    if (this.seenEntries.size > 100) {
      for (const [k, timestamp] of this.seenEntries.entries()) {
        if (now - timestamp > 60000) {
          this.seenEntries.delete(k);
        }
      }
    }

    // Suppress if seen within 250ms
    if (lastSeen && now - lastSeen < 250) {
      return true;
    }

    // Update timestamp for this entry
    this.seenEntries.set(key, now);
    return false;
  }

  /**
   * Log with custom level and metadata
   */
  log(
    level: DemoLogLevel,
    message: string,
    action?: DemoLogAction,
    meta: Partial<DemoLogMeta> = {}
  ): DemoLogEntry {
    // Check for duplicate suppression before creating entry
    if (!meta.suppressed && this.shouldSuppressDuplicate(level, message, meta)) {
      // Return a no-op entry for suppressed duplicates
      return {
        id: `suppressed-${Date.now()}`,
        ts: new Date().toISOString(),
        level,
        text: '',
        meta: {
          source: this.source,
          correlationId: '',
          requestId: '',
          timestamp: Date.now(),
          suppressed: true
        } as DemoLogMeta
      };
    }

    const entry = createDemoLogEntry(this.source, message, level, {
      component: this.component,
      action,
      correlationId: this.createCorrelationId(action),
      ...meta,
    });

    // Send to parent logger if available and not suppressed
    if (!meta.suppressed) {
      this.onLog?.(entry);
    }

    return entry;
  }

  /**
   * Log debug message - Gate debug logs behind NODE_ENV==='development' for Stage Tester
   */
  debug(message: string, action?: DemoLogAction, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    // Only log debug messages in development environment for Stage Tester
    if (import.meta.env?.NODE_ENV !== 'development' && this.source === 'stage_tester') {
      // Return a no-op log entry that won't be processed
      return {
        id: `noop-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'debug',
        text: '',
        meta: {
          source: this.source,
          correlationId: '',
          requestId: '',
          timestamp: Date.now(),
          suppressed: true
        } as DemoLogMeta
      };
    }
    return this.log('debug', message, action, meta);
  }

  /**
   * Log info message
   */
  info(message: string, action?: DemoLogAction, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.log('info', message, action, meta);
  }

  /**
   * Log warning message
   */
  warn(message: string, action?: DemoLogAction, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.log('warn', message, action, meta);
  }

  /**
   * Log error message
   */
  error(message: string, action?: DemoLogAction, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.log('error', message, action, meta);
  }

  /**
   * Log API request start
   */
  apiStart(url: string, method: string = 'GET', meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.info(`Starting ${method} request to ${url}`, 'fetch_start', {
      url,
      method,
      ...meta,
    });
  }

  /**
   * Log API request success
   */
  apiSuccess(
    url: string,
    duration: number,
    status: number = 200,
    meta?: Partial<DemoLogMeta>
  ): DemoLogEntry {
    return this.info(`✅ Request completed in ${duration}ms`, 'fetch_success', {
      url,
      duration,
      status,
      ...meta,
    });
  }

  /**
   * Log API request error
   */
  apiError(
    url: string,
    error: string,
    duration?: number,
    status?: number,
    meta?: Partial<DemoLogMeta>
  ): DemoLogEntry {
    return this.error(`❌ Request failed: ${error}`, 'fetch_error', {
      url,
      error,
      duration,
      status,
      ...meta,
    });
  }

  /**
   * Log API request cancellation
   */
  apiCancelled(url: string, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.debug('Request cancelled', 'fetch_cancelled', {
      url,
      ...meta,
    });
  }

  /**
   * Log user interaction
   */
  userAction(action: string, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.info(`User action: ${action}`, 'user_action', meta);
  }

  /**
   * Log permission denied
   */
  permissionDenied(resource: string, meta?: Partial<DemoLogMeta>): DemoLogEntry {
    return this.warn(`🔒 Access denied to ${resource}`, 'permission_denied', {
      resource,
      ...meta,
    });
  }

  /**
   * Create child logger with same configuration but different component
   */
  child(component: string): DemoLogger {
    return new DemoLogger(this.source, {
      component,
      onLog: this.onLog,
    });
  }
}

/**
 * React hook for consistent demo logging
 */
export function useDemoLogger(
  source: DemoLogSource,
  options: {
    component?: string;
    onLog?: (entry: DemoLogEntry) => void;
  } = {}
): DemoLogger {
  return new DemoLogger(source, options);
}

/**
 * Performance measurement utility
 */
export class DemoPerformanceTracker {
  private logger: DemoLogger;
  private startTime: number;
  private operation: string;

  constructor(logger: DemoLogger, operation: string) {
    this.logger = logger;
    this.operation = operation;
    this.startTime = performance.now();
  }

  /**
   * Finish tracking and log the duration
   */
  finish(success: boolean = true, meta?: Partial<DemoLogMeta>): number {
    const duration = Math.round(performance.now() - this.startTime);

    if (success) {
      this.logger.info(`⏱️ ${this.operation} completed in ${duration}ms`, undefined, {
        duration,
        operation: this.operation,
        ...meta,
      });
    } else {
      this.logger.warn(`⏱️ ${this.operation} failed after ${duration}ms`, undefined, {
        duration,
        operation: this.operation,
        ...meta,
      });
    }

    return duration;
  }
}

/**
 * Create performance tracker for operations
 */
export function trackDemoPerformance(logger: DemoLogger, operation: string): DemoPerformanceTracker {
  return new DemoPerformanceTracker(logger, operation);
}

/**
 * Filter log entries by level
 */
export function filterDemoLogsByLevel(
  entries: DemoLogEntry[],
  levels: DemoLogLevel[]
): DemoLogEntry[] {
  return entries.filter(entry => levels.includes(entry.level as DemoLogLevel));
}

/**
 * Filter log entries by source
 */
export function filterDemoLogsBySource(
  entries: DemoLogEntry[],
  sources: DemoLogSource[]
): DemoLogEntry[] {
  return entries.filter(entry => sources.includes(entry.meta.source));
}

/**
 * Filter log entries by action
 */
export function filterDemoLogsByAction(
  entries: DemoLogEntry[],
  actions: DemoLogAction[]
): DemoLogEntry[] {
  return entries.filter(entry =>
    entry.meta.action && actions.includes(entry.meta.action)
  );
}